/**
 * Adiciona itens via Cart API e redireciona ao checkout Shopify.
 * Trata erros JSON (422) que às vezes não falham em res.ok.
 */

import { CartAddEvent } from '@theme/events';
import { submitCartToCheckout } from './belabelly-cart-submit-checkout.js';

export { submitCartToCheckout };

/**
 * @param {unknown} data
 */
function cartAddFailed(data) {
  if (!data || typeof data !== 'object') return false;
  const record = /** @type {Record<string, unknown>} */ (data);
  if (record.status === 'error') return true;
  const status = Number(record.status);
  if (Number.isFinite(status) && status >= 400) return true;
  if (typeof record.description === 'string' && record.description.length > 0) return true;
  return false;
}

function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids.join(',');
}

/**
 * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
 * @param {{ errorMessage?: string; sections?: string; source?: string; sourceId?: string; productId?: string; itemCount?: number }} [options]
 */
export async function addItemsToCart(items, options = {}) {
  if (!items?.length) {
    throw new Error(options.errorMessage || 'Nenhum item para adicionar.');
  }

  const sections = options.sections || getCartSectionIds();
  const body = { items };
  if (sections) body.sections = sections;

  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok || cartAddFailed(data)) {
    const record = /** @type {Record<string, string>} */ (data);
    const msg =
      record.description ||
      record.message ||
      options.errorMessage ||
      'Não foi possível adicionar ao carrinho. Tente de novo.';
    throw new Error(msg);
  }

  let cart = data;
  try {
    const cartRes = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (cartRes.ok) {
      cart = await cartRes.json();
    }
  } catch {
    /* usa resposta do add */
  }

  const itemCount =
    options.itemCount ??
    items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  document.dispatchEvent(
    new CartAddEvent(cart, options.sourceId || 'belabelly-pdp', {
      source: options.source || 'belabelly-pdp',
      itemCount,
      productId: options.productId,
      sections: data.sections,
    })
  );

  return cart;
}

export async function addItemsAndGoCart(items, options = {}) {
  await addItemsToCart(items, options);
  const cartUrl =
    options.cartUrl ||
    (typeof window !== 'undefined' && window.Theme?.routes?.cart_url) ||
    '/cart';
  window.location.assign(cartUrl);
  return null;
}

/**
 * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
 * @param {{ checkoutUrl?: string; errorMessage?: string; sections?: string }} [options]
 */
export async function addItemsAndGoCheckout(items, options = {}) {
  if (!items?.length) {
    throw new Error(options.errorMessage || 'Nenhum item para comprar.');
  }

  const body = { items };
  if (options.sections) body.sections = options.sections;
  else {
    const sections = getCartSectionIds();
    if (sections) body.sections = sections;
  }

  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok || cartAddFailed(data)) {
    const record = /** @type {Record<string, string>} */ (data);
    const msg =
      record.description ||
      record.message ||
      options.errorMessage ||
      'Não foi possível adicionar ao carrinho. Tente de novo.';
    throw new Error(msg);
  }

  if (options.checkoutUrl) {
    window.location.assign(options.checkoutUrl);
    return data;
  }

  submitCartToCheckout();
  return data;
}

if (typeof window !== 'undefined') {
  window.BelabellyCheckoutGo = {
    addItemsToCart,
    addItemsAndGoCart,
    addItemsAndGoCheckout,
    cartAddFailed,
    submitCartToCheckout,
  };
}
